import MigrateUserPermissionsToRole from 'components/Limitation/LimitationManager/MigrateUserPermissionsToRole';
import { PureComponent } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { MapDispatchToProps, connect, MapStateToProps } from 'react-redux';
import { isCompanyAdmin } from 'store/companies/companiesUtils';
import { RootState } from 'store/store';
import {
  requireDocument,
  requireDocumentFn,
  updateItem,
  updateItemFn,
} from 'store/providers/providerSlice';
import Modal from 'components/Modal';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import messages from './Messages';
import { display } from 'components/ErrorView/ErrorDialog';
import RoleLimitation from './RoleLimitation';
import LimitationManager from 'components/Limitation/LimitationManager/LimitationManager';
import nofity from 'components/Notify';
import SingleItemTokenManagerPanel from './SingleItemTokenManagerPanel/SingleItemTokenManagerPanel';
import { appDispatch } from 'store/store';
import {
  IProviderItemView,
  IProviderItemViewWithData,
} from 'store/providers/providerTypes';
import { ICompany } from 'store/companies/companiesTypes';
import Button from '../../../../components/Button';
import Icon from '../../../../components/Icon';
import { viewGuide } from '../../../../store/siteGuide/SiteGuideSlice';
import i18n from '../../../../i18n';

interface StateToProps {
  companyRoles: ICompany['roles'];
  companyRolesOptions?: Array<{
    value: number;
    label: string;
  }>;
  companyMembers: ICompany['members'];
  companyMembersOptions?: Array<{
    value: number;
    label: string;
  }>;
  requiredUsers: Array<number>;
  isAdmin: boolean;
}

interface IDispatchToProps {
  updateItem: (item: IProviderItemView) => ReturnType<typeof updateItemFn>;
  requireDocument: (id: number) => ReturnType<typeof requireDocumentFn>;
  requireSeveralUser: (
    usersList: Array<number>
  ) => ReturnType<typeof requireSeveralUserFn>;
  viewGuide: (id: string) => void;
}

interface IOwnProps {
  item: IProviderItemViewWithData;
  onComplete: () => void;
  managingCompanyId?: number;
  limitAccessToCompany: boolean;
}

interface IComponentStates {
  selectedTab: string;
}

type IComponentProps = IOwnProps & IDispatchToProps & StateToProps;

class ChangeLimitationModal extends PureComponent<
  IComponentProps,
  IComponentStates
> {
  modalTabs = [
    {
      id: 'userLimitation',
      title: i18n.t('userLimitation', { ns: 'documentManager' }) as string,
    },
    {
      id: 'tokens',
      title: i18n.t('singleItemAccessTokenLinks', {
        ns: 'documentManager',
      }) as string,
    },
    {
      id: 'migrateRole',
      title: i18n.t('migrateUserPermissionsToRole', {
        ns: 'documentManager',
      }) as string,
    },
  ];

  state: IComponentStates = {
    selectedTab: 'userLimitation',
  };

  componentDidMount() {
    if (this.props.requiredUsers.length) {
      this.props.requireSeveralUser(this.props.requiredUsers);
    }
  }

  handleSelectATab = (selectedTab: number | string) => {
    this.setState({ selectedTab: selectedTab as string });
  };

  handleSaveFastAccess = async (fastAccessRoles: string[]) => {
    const { data: item } = this.props.item;
    const lastFastAccess = item.fastAccess;
    if (
      lastFastAccess.length === fastAccessRoles.length &&
      lastFastAccess.every((f) => fastAccessRoles.includes(f))
    ) {
      nofity(messages.notChanged, 'warning');
      return;
    }

    const newItem = cloneDeep(item);
    newItem.fastAccess = fastAccessRoles;
    try {
      await this.props.updateItem(newItem);
      nofity(i18n.t('saveSuccess', { ns: 'documentManager' }));
    } catch (err) {
      display(err);
    }
  };

  render() {
    const {
      item: { data: item },
      onComplete,
      managingCompanyId,
      limitAccessToCompany,
      companyRoles,
      companyRolesOptions,
    } = this.props;
    const { selectedTab } = this.state;
    let tabs = managingCompanyId
      ? this.modalTabs
      : this.modalTabs.filter((it) => it.id !== 'roleLimitation');
    tabs = tabs.filter((it) => it.id !== 'migrateRole' || this.props.isAdmin);
    tabs = tabs.filter((it) => it.id !== 'tokens' || item.type !== 'folder');
    const getItemAgainToUpdateEffectivePermission = () => {
      this.props.requireDocument(item.id);
      onComplete();
    };
    return (
      <Modal
        tabs={tabs}
        title={item.name ? messages.changeLimitationOf(item.name) : ''}
        onClose={getItemAgainToUpdateEffectivePermission}
        isActive={true}
        selectedTab={selectedTab}
        onSelectATab={this.handleSelectATab}
        width="90%"
        sideElements={
          <div>
            <Button onClick={() => this.props.viewGuide('documentPermissions')}>
              <Icon type={'info'} />
              {i18n.t('permissionHint', { ns: 'documentManager' }) as string}
            </Button>
          </div>
        }
      >
        {selectedTab === 'userLimitation' && (
          <LimitationManager
            item={item}
            companyId={managingCompanyId}
            companyRoles={companyRoles}
            companyRolesOptions={companyRolesOptions}
            companyMembers={this.props.companyMembers}
            companyMembersOptions={this.props.companyMembersOptions}
            limitAccessToCompany={limitAccessToCompany}
          />
        )}
        {managingCompanyId && selectedTab === 'roleLimitation' && (
          <RoleLimitation
            companyId={managingCompanyId}
            companyRoles={companyRoles}
            companyRolesOptions={companyRolesOptions}
            fastAccess={item.fastAccess}
            onSubmitFastAccess={this.handleSaveFastAccess}
          />
        )}
        {this.props.isAdmin && selectedTab === 'migrateRole' && (
          <MigrateUserPermissionsToRole
            companyId={managingCompanyId}
            companyRolesOptions={companyRolesOptions}
            companyMembersOptions={this.props.companyMembersOptions}
          />
        )}
        {selectedTab === 'tokens' && (
          <SingleItemTokenManagerPanel item={item} />
        )}
      </Modal>
    );
  }
}

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = (
  dispatch
) => {
  return {
    updateItem: (item: IProviderItemView) => {
      return appDispatch(updateItem({ item }));
    },
    requireDocument: (id: number) =>
      appDispatch(requireDocument({ documentId: id })),
    requireSeveralUser: (usersList: Array<number>) =>
      appDispatch(requireSeveralUser({ usersList })),
    viewGuide: (key: string) => dispatch(viewGuide(key)),
  };
};

const mapStateToProps: MapStateToProps<StateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  let companyRolesOptions: Array<{
    value: number;
    label: string;
  }> = [];
  let companyRoles: ICompany['roles'] = {};
  if (props.managingCompanyId) {
    companyRoles = state.companies.data[props.managingCompanyId].roles;
    if (companyRoles && Object.keys(companyRoles).length > 0) {
      companyRolesOptions = Object.values(companyRoles).reduce(
        (temp, roleItem) => {
          if (!roleItem.isProjectRole)
            temp.push({
              value: roleItem.id,
              label: roleItem.displayName!,
            });
          return temp;
        },
        [] as Array<{
          value: number;
          label: string;
        }>
      );
    }
  }
  const usersList = state.users.map;
  let companyMembersOptions: Array<{
    value: number;
    label: string;
  }> = [];
  let companyMembers: ICompany['members'] = {};
  if (props.managingCompanyId) {
    companyMembers = state.companies.data[props.managingCompanyId].members;
    if (companyMembers && Object.keys(companyMembers).length > 0) {
      companyMembersOptions = Object.values(companyMembers).reduce(
        (temp, roleItem) => {
          temp.push({
            value: roleItem.userId,
            label: usersList[roleItem.userId]?.name || '',
          });
          return temp;
        },
        [] as Array<{
          value: number;
          label: string;
        }>
      );
    }
  }

  const requiredUsers = companyMembersOptions
    .filter((itm) => {
      return itm.value && !itm.label;
    })
    .map((itm) => itm.value);

  const isAdmin =
    state.thisUser && props.managingCompanyId
      ? isCompanyAdmin(
          state.thisUser,
          state.companies.data[props.managingCompanyId]
        )
      : false;

  return {
    companyRoles,
    companyRolesOptions,
    companyMembers,
    companyMembersOptions,
    requiredUsers,
    isAdmin,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChangeLimitationModal);
